import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import KPICard from "../components/KPICard";
import RAGBadge from "../components/RAGBadge";
import { useTheme } from "../DataContext";
import { DEMO_PM_SCORES } from "../demoData";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, ZAxis, Cell,
} from "recharts";

export default function PMWorkforceQuality() {
  const { theme } = useTheme();
  const [selected, setSelected] = useState(DEMO_PM_SCORES[0]);

  const avgOverall    = Math.round(DEMO_PM_SCORES.reduce((s, p) => s + p.overall, 0) / DEMO_PM_SCORES.length);
  const topPerformers = DEMO_PM_SCORES.filter(p => p.overall >= 90).length;
  const lowPerformers = DEMO_PM_SCORES.filter(p => p.overall < 65).length;
  const declining     = DEMO_PM_SCORES.filter(p => p.trend === "down").length;

  const scoreColor = (s) => s >= 90 ? theme.success : s >= 75 ? theme.accent : s >= 60 ? theme.warning : theme.danger;
  const trendIcon  = (t) => t === "up" ? "▲" : t === "down" ? "▼" : "—";
  const trendColor = (t) => t === "up" ? theme.success : t === "down" ? theme.danger : theme.textMuted;

  const radarData = selected ? [
    { subject: "Schedule Quality", A: selected.schedQuality  },
    { subject: "Data Integrity",   A: selected.dataIntegrity },
    { subject: "Delivery Pred.",   A: selected.deliveryPred  },
    { subject: "Overall",          A: selected.overall       },
  ] : [];

  const scatterData = DEMO_PM_SCORES.map(p => ({
    x: p.schedQuality,
    y: p.dataIntegrity,
    z: p.overall,
    name: p.pm,
    trend: p.trend,
    overall: p.overall,
  }));

  return (
    <PageLayout
      title="PM Workforce Quality"
      subtitle="PM performance scoring — schedule quality, data integrity, delivery predictability — P-09"
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        <KPICard label="Portfolio Avg Score" value={`${avgOverall}%`} icon="◈" color={scoreColor(avgOverall)}  sub="Composite PM score" />
        <KPICard label="Top Performers"      value={topPerformers}     icon="★" color={theme.success}  sub="Score ≥ 90%" />
        <KPICard label="Needs Improvement"   value={lowPerformers}     icon="⚠" color={theme.danger}   sub="Score < 65%" />
        <KPICard label="Declining Trend"     value={declining}         icon="▼" color={theme.warning}  sub="Performance dropping" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        {/* PM Score Leaderboard Bar */}
        <div className="pmo-card">
          <div className="section-title">PM Performance Leaderboard</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={[...DEMO_PM_SCORES].sort((a, b) => b.overall - a.overall).map(p => ({
                name: p.pm.split(" ")[0],
                full: p.pm,
                score: p.overall,
                trend: p.trend,
              }))}
              layout="vertical"
              margin={{ top: 0, right: 60, bottom: 0, left: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}44`} horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <YAxis type="category" dataKey="name" tick={{ fill: theme.textSecondary, fontSize: 10 }} width={55} />
              <Tooltip
                content={({ payload }) => {
                  if (!payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div style={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, padding: "8px 12px", borderRadius: "8px", fontSize: "11px" }}>
                      <div style={{ color: theme.textPrimary, fontWeight: 600 }}>{d.full}</div>
                      <div>Score: <span style={{ color: scoreColor(d.score) }}>{d.score}%</span></div>
                      <div>Trend: <span style={{ color: trendColor(d.trend) }}>{trendIcon(d.trend)} {d.trend}</span></div>
                    </div>
                  );
                }}
              />
              <Bar dataKey="score" name="Score" radius={[0,4,4,0]}>
                {[...DEMO_PM_SCORES].sort((a, b) => b.overall - a.overall).map((p, i) => (
                  <Cell key={i} fill={scoreColor(p.overall)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Schedule Quality vs Data Integrity Scatter */}
        <div className="pmo-card">
          <div className="section-title">Schedule Quality vs Data Integrity</div>
          <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "6px" }}>Bubble size = overall score</div>
          <ResponsiveContainer width="100%" height={260}>
            <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis type="number" dataKey="x" name="Schedule Quality" domain={[40, 100]}
                tick={{ fill: theme.textMuted, fontSize: 9 }}
                label={{ value: "Schedule Quality %", fill: theme.textMuted, fontSize: 10, position: "bottom", offset: -5 }} />
              <YAxis type="number" dataKey="y" name="Data Integrity" domain={[60, 100]}
                tick={{ fill: theme.textMuted, fontSize: 9 }}
                label={{ value: "Data Integrity %", angle: -90, fill: theme.textMuted, fontSize: 10 }} />
              <ZAxis type="number" dataKey="z" range={[40, 150]} />
              <Tooltip content={({ payload }) => {
                if (!payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, padding: "8px 12px", borderRadius: "8px", fontSize: "11px" }}>
                    <div style={{ color: scoreColor(d.overall), fontWeight: 600 }}>{d.name}</div>
                    <div>Sched: {d.x}% · Data: {d.y}%</div>
                    <div>Overall: {d.overall}%</div>
                  </div>
                );
              }} />
              <Scatter
                data={scatterData}
                shape={(props) => {
                  const { cx, cy, r, payload } = props;
                  const color = scoreColor(payload.overall);
                  return (
                    <g onClick={() => setSelected(DEMO_PM_SCORES.find(p => p.pm === payload.name))} style={{ cursor: "pointer" }}>
                      <circle cx={cx} cy={cy} r={Math.max(r / 2, 7)} fill={`${color}55`} stroke={color} strokeWidth={1.5} />
                    </g>
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Selected PM Detail */}
      {selected && (
        <div className="pmo-card" style={{ marginBottom: "20px" }}>
          <div className="section-title">{selected.pm} — Performance Profile</div>
          <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "20px" }}>
            <ResponsiveContainer width="100%" height={200}>
              <RadarChart data={radarData}>
                <PolarGrid stroke={`${theme.cardBorder}88`} />
                <PolarAngleAxis dataKey="subject" tick={{ fill: theme.textMuted, fontSize: 9 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} />
                <Radar dataKey="A" stroke={scoreColor(selected.overall)} fill={scoreColor(selected.overall)} fillOpacity={0.25} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
              {[
                { l: "Overall Score",      v: `${selected.overall}%`,      c: scoreColor(selected.overall) },
                { l: "Schedule Quality",   v: `${selected.schedQuality}%`, c: scoreColor(selected.schedQuality) },
                { l: "Data Integrity",     v: `${selected.dataIntegrity}%`,c: scoreColor(selected.dataIntegrity) },
                { l: "Delivery Predictability", v: `${selected.deliveryPred}%`, c: scoreColor(selected.deliveryPred) },
                { l: "Trend",              v: `${trendIcon(selected.trend)} ${selected.trend.toUpperCase()}`, c: trendColor(selected.trend) },
                { l: "Active Projects",    v: selected.projects.join(", "), c: theme.textSecondary },
              ].map((item, i) => (
                <div key={i} style={{ padding: "10px 14px", background: theme.bg, borderRadius: "8px", border: `1px solid ${theme.cardBorder}44` }}>
                  <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "4px" }}>{item.l}</div>
                  <div style={{ fontSize: "16px", color: item.c, fontWeight: 700, fontFamily: item.l === "Active Projects" ? "inherit" : "'Orbitron',monospace" }}>{item.v}</div>
                </div>
              ))}
              <div style={{ gridColumn: "1/-1", padding: "10px 14px", background: `${scoreColor(selected.overall)}10`, border: `1px solid ${scoreColor(selected.overall)}33`, borderRadius: "8px" }}>
                <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "4px" }}>AI Coaching Recommendation</div>
                <div style={{ fontSize: "12px", color: theme.textSecondary }}>
                  {selected.overall >= 90
                    ? "Exceptional performance. Consider for PMO mentor role. Eligible for advanced portfolio assignment."
                    : selected.overall >= 75
                    ? "Good performance with specific improvement areas. Focus coaching on data integrity and reporting cadence."
                    : selected.overall >= 60
                    ? "Performance below portfolio average. Recommend bi-weekly PMO check-in and schedule quality workshop."
                    : "Critical intervention required. Immediate PMO Director review. Consider reassigning project or adding co-PM support."}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full PM Score Table */}
      <div className="pmo-card">
        <div className="section-title">PM Workforce Quality Register</div>
        <table>
          <thead>
            <tr>{["PM Name","Projects","Sched. Quality","Data Integrity","Delivery Pred.","Overall Score","Trend","Assessment"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {[...DEMO_PM_SCORES].sort((a, b) => b.overall - a.overall).map((p, i) => (
              <tr key={i} style={{ cursor: "pointer" }} onClick={() => setSelected(p)}>
                <td style={{ color: theme.textPrimary, fontWeight: selected?.pm === p.pm ? 600 : 400 }}>{p.pm}</td>
                <td style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: "11px" }}>{p.projects.join(", ")}</td>
                <td style={{ color: scoreColor(p.schedQuality), fontFamily: "'Share Tech Mono',monospace" }}>{p.schedQuality}%</td>
                <td style={{ color: scoreColor(p.dataIntegrity), fontFamily: "'Share Tech Mono',monospace" }}>{p.dataIntegrity}%</td>
                <td style={{ color: scoreColor(p.deliveryPred), fontFamily: "'Share Tech Mono',monospace" }}>{p.deliveryPred}%</td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ flex: 1, height: "6px", background: theme.cardBorder, borderRadius: "3px", maxWidth: "80px" }}>
                      <div style={{ width: `${p.overall}%`, height: "100%", background: scoreColor(p.overall), borderRadius: "3px" }} />
                    </div>
                    <span style={{ color: scoreColor(p.overall), fontFamily: "'Orbitron',monospace", fontSize: "13px", fontWeight: 700 }}>{p.overall}%</span>
                  </div>
                </td>
                <td style={{ color: trendColor(p.trend), fontWeight: 700 }}>{trendIcon(p.trend)} {p.trend}</td>
                <td>
                  <RAGBadge
                    status={p.overall >= 90 ? "green" : p.overall >= 75 ? "blue" : p.overall >= 60 ? "amber" : "red"}
                    label={p.overall >= 90 ? "EXCELLENT" : p.overall >= 75 ? "GOOD" : p.overall >= 60 ? "IMPROVE" : "ACTION"}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
